// src/mobile_pages/const/UserDefine.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ChevronDown,
  Package,
  Wrench,
  Users,
  Plus,
  Trash2,
  Save
} from "lucide-react";
import {
  getMaterials,
  getEquipments,
  getPersonnels,
  deleteMaterial,
  deleteEquipment,
  deletePersonnel,
  updateMaterial,
  updateEquipment,
  updatePersonnel,
  createMaterial,
  createEquipment,
  createPersonnel
} from "../../api/const";

export default function UserDefine() {
  const { id } = useParams();
  const project = id;

  // 현재값 (로컬 상태)
  const [materials, setMaterials] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [personnels, setPersonnels] = useState([]);

  // 원본 (서버에서 불러온 상태)
  const [materials0, setMaterials0] = useState([]);
  const [equipments0, setEquipments0] = useState([]);
  const [personnels0, setPersonnels0] = useState([]);

  // 삭제 대기 목록 (서버에서 삭제해야 할 ID들)
  const [deletedMaterials, setDeletedMaterials] = useState([]);
  const [deletedEquipments, setDeletedEquipments] = useState([]);
  const [deletedPersonnels, setDeletedPersonnels] = useState([]);

  const [saving, setSaving] = useState(false);

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    material: true,
    equipment: true,
    personnel: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 데이터 불러오기
  const fetchAll = async () => {
    const [mats, eqs, pers] = await Promise.all([
      getMaterials(project),
      getEquipments(project),
      getPersonnels(project),
    ]);
    setMaterials(mats); setMaterials0(mats);
    setEquipments(eqs); setEquipments0(eqs);
    setPersonnels(pers); setPersonnels0(pers);
    // 삭제 대기 목록 초기화
    setDeletedMaterials([]);
    setDeletedEquipments([]);
    setDeletedPersonnels([]);
  };

  useEffect(() => { fetchAll(); }, [project]);

  // 로컬 추가 (서버에 즉시 저장하지 않음)
  const handleAddMaterial = () => {
    const tempId = `temp_${Date.now()}`;
    setMaterials(prev => [...prev, {
      id: tempId,
      name: "신규 자재",
      spec: "-",
      unit: "-",
      _isNew: true
    }]);
  };

  const handleAddEquipment = () => {
    const tempId = `temp_${Date.now()}`;
    setEquipments(prev => [...prev, {
      id: tempId,
      name: "신규 장비",
      spec: "-",
      _isNew: true
    }]);
  };

  const handleAddPersonnel = () => {
    const tempId = `temp_${Date.now()}`;
    setPersonnels(prev => [...prev, {
      id: tempId,
      work_type: "신규 공종",
      category: "-",
      _isNew: true
    }]);
  };

  // 로컬 삭제 (서버에 즉시 삭제하지 않음)
  const handleDelete = (type, rid) => {
    if (!window.confirm("삭제하시겠습니까?")) return;

    if (type === "material") {
      const item = materials.find(m => m.id === rid);
      if (item && !item._isNew) {
        setDeletedMaterials(prev => [...prev, rid]);
      }
      setMaterials(prev => prev.filter(m => m.id !== rid));
    }
    if (type === "equipment") {
      const item = equipments.find(e => e.id === rid);
      if (item && !item._isNew) {
        setDeletedEquipments(prev => [...prev, rid]);
      }
      setEquipments(prev => prev.filter(e => e.id !== rid));
    }
    if (type === "personnel") {
      const item = personnels.find(p => p.id === rid);
      if (item && !item._isNew) {
        setDeletedPersonnels(prev => [...prev, rid]);
      }
      setPersonnels(prev => prev.filter(p => p.id !== rid));
    }
  };

  // 인라인 변경 핸들러
  const onMatChange = (id, field, value) =>
    setMaterials(prev => prev.map(m => (m.id === id ? { ...m, [field]: value } : m)));
  const onEqChange = (id, field, value) =>
    setEquipments(prev => prev.map(e => (e.id === id ? { ...e, [field]: value } : e)));
  const onPerChange = (id, field, value) =>
    setPersonnels(prev => prev.map(p => (p.id === id ? { ...p, [field]: value } : p)));

  // 변경 여부 계산
  const isChanged = (a, b, keys) => keys.some(k => (a?.[k] ?? null) !== (b?.[k] ?? null));
  const mapById = (arr) => Object.fromEntries(arr.map(x => [x.id, x]));

  const hasChanges = useMemo(() => {
    // 1. 삭제 대기 항목이 있는지
    if (deletedMaterials.length > 0 || deletedEquipments.length > 0 || deletedPersonnels.length > 0) {
      return true;
    }

    // 2. 새로 추가된 항목이 있는지
    if (materials.some(m => m._isNew) || equipments.some(e => e._isNew) || personnels.some(p => p._isNew)) {
      return true;
    }

    // 3. 기존 항목이 수정되었는지
    const m0 = mapById(materials0), e0 = mapById(equipments0), p0 = mapById(personnels0);
    const matsChanged = materials.filter(m => !m._isNew && isChanged(m0[m.id], m, ["name", "spec", "unit"])).length > 0;
    const eqsChanged = equipments.filter(e => !e._isNew && isChanged(e0[e.id], e, ["name", "spec"])).length > 0;
    const persChanged = personnels.filter(p => !p._isNew && isChanged(p0[p.id], p, ["work_type", "category"])).length > 0;

    return matsChanged || eqsChanged || persChanged;
  }, [materials, equipments, personnels, materials0, equipments0, personnels0, deletedMaterials, deletedEquipments, deletedPersonnels]);

  // 저장: 모든 변경사항 일괄 처리
  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);

    try {
      // 1. 삭제 처리
      await Promise.all([
        ...deletedMaterials.map(id => deleteMaterial(id)),
        ...deletedEquipments.map(id => deleteEquipment(id)),
        ...deletedPersonnels.map(id => deletePersonnel(id)),
      ]);

      // 2. 신규 항목 생성
      const newMaterials = materials.filter(m => m._isNew);
      const newEquipments = equipments.filter(e => e._isNew);
      const newPersonnels = personnels.filter(p => p._isNew);

      await Promise.all([
        ...newMaterials.map(m => createMaterial({ name: m.name, spec: m.spec, unit: m.unit, project })),
        ...newEquipments.map(e => createEquipment({ name: e.name, spec: e.spec, project })),
        ...newPersonnels.map(p => createPersonnel({ work_type: p.work_type, category: p.category, project })),
      ]);

      // 3. 기존 항목 수정
      const m0 = mapById(materials0), e0 = mapById(equipments0), p0 = mapById(personnels0);
      const changedMats = materials.filter(m => !m._isNew && isChanged(m0[m.id], m, ["name", "spec", "unit"]));
      const changedEqs = equipments.filter(e => !e._isNew && isChanged(e0[e.id], e, ["name", "spec"]));
      const changedPers = personnels.filter(p => !p._isNew && isChanged(p0[p.id], p, ["work_type", "category"]));

      await Promise.all([
        ...changedMats.map(m => updateMaterial(m.id, { name: m.name, spec: m.spec, unit: m.unit, project })),
        ...changedEqs.map(e => updateEquipment(e.id, { name: e.name, spec: e.spec, project })),
        ...changedPers.map(p => updatePersonnel(p.id, { work_type: p.work_type, category: p.category, project })),
      ]);

      // 4. 서버에서 다시 불러오기
      await fetchAll();
      alert("저장 완료!");
    } catch (err) {
      console.error(err);
      alert("저장 실패. 잠시 후 다시 시도해 주세요.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 모바일 본문 영역 */}
      <main className="px-4 pt-4 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리</p>
          <h2 className="text-xl font-bold text-gray-900">사용자 정의 목록</h2>
        </header>

        {/* 요약 카드 */}
        <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-gray-100 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600">등록 현황</span>
            {hasChanges && (
              <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium">
                변경사항 있음
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center py-2 rounded-lg bg-white shadow-sm">
              <div className="text-xl font-bold text-gray-800">{materials.length}</div>
              <div className="text-[10px] text-gray-500">자재</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-white shadow-sm">
              <div className="text-xl font-bold text-gray-800">{equipments.length}</div>
              <div className="text-[10px] text-gray-500">장비</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-white shadow-sm">
              <div className="text-xl font-bold text-gray-800">{personnels.length}</div>
              <div className="text-[10px] text-gray-500">인원</div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* 1. 자재 목록 */}
          <Section
            title="자재 목록"
            expanded={expandedSections.material}
            onToggle={() => toggleSection("material")}
            icon={<Package size={16} className="text-purple-500" />}
            badge={`${materials.length}개`}
            onAdd={handleAddMaterial}
          >
            <div className="space-y-3">
              {materials.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">등록된 자재가 없습니다.</p>
              ) : (
                materials.map((m) => (
                  <div key={m.id} className={`rounded-lg border p-3 space-y-2 ${m._isNew ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">
                        {m._isNew && <span className="text-blue-600">[신규] </span>}자재
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDelete("material", m.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={m.name ?? ""}
                      onChange={(e) => onMatChange(m.id, "name", e.target.value)}
                      placeholder="자재명"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={m.spec ?? ""}
                        onChange={(e) => onMatChange(m.id, "spec", e.target.value)}
                        placeholder="규격"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                      <input
                        type="text"
                        value={m.unit ?? ""}
                        onChange={(e) => onMatChange(m.id, "unit", e.target.value)}
                        placeholder="단위"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Section>

          {/* 2. 장비 목록 */}
          <Section
            title="장비 목록"
            expanded={expandedSections.equipment}
            onToggle={() => toggleSection("equipment")}
            icon={<Wrench size={16} className="text-orange-500" />}
            badge={`${equipments.length}개`}
            onAdd={handleAddEquipment}
          >
            <div className="space-y-3">
              {equipments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">등록된 장비가 없습니다.</p>
              ) : (
                equipments.map((e) => (
                  <div key={e.id} className={`rounded-lg border p-3 space-y-2 ${e._isNew ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">
                        {e._isNew && <span className="text-blue-600">[신규] </span>}장비
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDelete("equipment", e.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={e.name ?? ""}
                      onChange={(ev) => onEqChange(e.id, "name", ev.target.value)}
                      placeholder="장비명"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={e.spec ?? ""}
                      onChange={(ev) => onEqChange(e.id, "spec", ev.target.value)}
                      placeholder="규격"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                    />
                  </div>
                ))
              )}
            </div>
          </Section>

          {/* 3. 인원 목록 */}
          <Section
            title="인원 목록"
            expanded={expandedSections.personnel}
            onToggle={() => toggleSection("personnel")}
            icon={<Users size={16} className="text-teal-500" />}
            badge={`${personnels.length}개`}
            onAdd={handleAddPersonnel}
          >
            <div className="space-y-3">
              {personnels.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">등록된 인원이 없습니다.</p>
              ) : (
                personnels.map((p) => (
                  <div key={p.id} className={`rounded-lg border p-3 space-y-2 ${p._isNew ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">
                        {p._isNew && <span className="text-blue-600">[신규] </span>}인원
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDelete("personnel", p.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={p.work_type ?? ""}
                        onChange={(e) => onPerChange(p.id, "work_type", e.target.value)}
                        placeholder="공종"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                      <input
                        type="text"
                        value={p.category ?? ""}
                        onChange={(e) => onPerChange(p.id, "category", e.target.value)}
                        placeholder="구분"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`w-full h-11 rounded-full flex items-center justify-center gap-2 text-sm font-medium transition-all active:scale-[0.97] ${hasChanges
              ? "bg-blue-500 text-white hover:bg-blue-600"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
        >
          <Save size={18} />
          {saving ? "저장 중..." : hasChanges ? "변경사항 저장" : "변경사항 없음"}
        </button>
      </footer>
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, badge, icon, onAdd, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onAdd && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors"
            >
              <Plus size={12} />
              추가
            </button>
          )}
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
              }`}
          />
        </div>
      </button>
      {expanded && (
        <div className="p-4">{children}</div>
      )}
    </section>
  );
}
